import { useQuery } from '@apollo/client';

import type {
  CalculatedCreativeDataForAdByAssetAndCountryListQuery,
  CalculatedCreativeDataForAdByAssetAndCountryListQueryVariables,
  CalculatedCreativeDataWithCountriesFragment,
} from '../../apis/graphql';
import { GET_CALCULATED_CREATIVE_DATA_FOR_AD_BY_ASSET_AND_COUNTRY_LIST } from '../../apis/graphql/common/queries/ad/getCalculatedCreativeDataForAdByAssetAndCountryList';
import type { CreativesBuilderValues } from '../../common/AdForm/CreativeBuilders/types';
import { getAssetIds } from '../../common/AdForm/utils';
import type { Nullable } from '../../models';

export type CalculatedCreativeData = Omit<CalculatedCreativeDataWithCountriesFragment, 'approvedCountries'> & {
  approvedCountries?: CalculatedCreativeDataWithCountriesFragment['approvedCountries'];
};

type Props = {
  creative: Nullable<CreativesBuilderValues>;
  publisherList: string[];
  countryList: string[];
  skip?: boolean;
};

export type UseCalculatedCreativeDataResult = {
  calculatedCreativeData?: CalculatedCreativeData;
  loading: boolean;
};

export const useCalculatedCreativeData = ({
  creative,
  publisherList,
  countryList,
  skip,
}: Props): UseCalculatedCreativeDataResult => {
  const assetIds = getAssetIds(creative);
  const creativeType = creative?.type ?? '';
  const baseSkip = skip || !assetIds.length || !creativeType;

  const { data: countryListData, loading: countryListLoading } = useQuery<
    CalculatedCreativeDataForAdByAssetAndCountryListQuery,
    CalculatedCreativeDataForAdByAssetAndCountryListQueryVariables
  >(GET_CALCULATED_CREATIVE_DATA_FOR_AD_BY_ASSET_AND_COUNTRY_LIST, {
    variables: {
      assetIdList: assetIds,
      creativeType,
      publisherList,
      countryList,
    },
    skip: baseSkip || !countryList.length,
  });

  const calculatedCreativeData = countryListData?.getCalculatedCreativeDataForAdByAssetAndCountryList;

  return {
    calculatedCreativeData,
    loading: countryListLoading,
  };
};
